import { Icon, PageTitle, Select, SelectOption, useIntl } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { FC, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { groupSensorBatchesDispatchedRows } from "./dispatch-history-helper"
import { DispatchedRow } from "features/iot/stock-management/models/stock-management-models"
import styles from "./dispatch-history-view.module.css"
import sensorBatchStore from "features/iot/sensor-batch/stores/sensor-batch-store"
import ListView, { TemplateItem } from "components/utils/list-view/list-view"
import { itemViewDetailsContainerStyles } from "components/common-style/common-style"
import { convertValueToOption } from "components/common/select-component-helper"

const DispatchHistortView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const params = useParams()
    const navigate = useNavigate()
    const { sensorBatches, allPartNumbers, getAllSensors } = sensorBatchStore
    const [dataTable, setDataTable] = useState<DispatchedRow[]>([])
    const [searchInput, setSearchInput] = useState<SelectOption>({ text: "", value: "" })
    const [listOfPartNumbers, setListOfPartNumbers] = useState<SelectOption[]>([])

    const loadHardwareVariants = () => {
        const partNumbers = allPartNumbers
        if (partNumbers.length > 0) {
            setListOfPartNumbers(
                partNumbers.map(h => ({
                    text: h,
                    value: h,
                })),
            )
        }
    }

    // Sync state with URL params on initial load or when params change
    useEffect(() => {
        if (params.partNumber && params.partNumber !== searchInput.text) {
            setSearchInput({ text: params.partNumber, value: params.partNumber })
        }
    }, [params.partNumber])

    // Navigate when search input changes due to user action
    useEffect(() => {
        if (searchInput.text && searchInput.text !== params.partNumber) {
            navigate(`/stock-management/dispatch-history/${searchInput.text}`)
        }
    }, [searchInput])

    useEffect(() => {
        const data = groupSensorBatchesDispatchedRows(searchInput.text)
        setDataTable(data)
    }, [searchInput, sensorBatches, getAllSensors])

    useEffect(() => {
        loadHardwareVariants()
    }, [sensorBatches])

    const onSearchChange = (resultValue: SelectOption) => {
        setSearchInput(resultValue)
    }

    const onClickRow = (stockAllocation: DispatchedRow) => {
        navigate(`/stock-management/dispatched-sensors/${searchInput.text}/${stockAllocation.customer}/${stockAllocation.site}`)
    }

    const tableTemplate: TemplateItem<DispatchedRow>[] = [
        {
            headingKey: "headings.customer",
            cellFunc: (s: DispatchedRow) => {
                return s.customer
            },
            isSearchable: true,
        },
        {
            headingKey: "headings.site",
            cellFunc: (s: DispatchedRow) => {
                return s.site
            },
        },
        {
            headingKey: "headings.quantity",
            cellFunc: (s: DispatchedRow) => {
                return s.quantity
            },
        },
    ]

    return (
        <>
            <PageTitle onPathChange={navigate} title={formatMessage("dispatchHistory.title")} />

            <div style={itemViewDetailsContainerStyles}>
                <Select
                    id='SockAllocationSearchInput'
                    icon={<Icon className='icon-search-16' />}
                    label={formatMessage("headings.partNumber")}
                    onChange={value => onSearchChange(convertValueToOption(value, listOfPartNumbers))}
                    options={listOfPartNumbers}
                    value={searchInput.text}
                />

                <div className={styles.tableContainerStyles}>
                    <ListView
                        noneYetKey={"stockAllocation.nonYet"}
                        template={tableTemplate}
                        data={dataTable}
                        onClickRow={(s: DispatchedRow) => onClickRow(s)}
                    />
                </div>
            </div>
        </>
    )
})

export default DispatchHistortView
